import { extendTheme, defineStyleConfig } from "@chakra-ui/react"
import '@fontsource/poppins/100.css';
import '@fontsource/poppins/200.css';
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';
import '@fontsource/poppins/900.css';

// Supports weights 100-900
import '@fontsource-variable/montserrat';

// ─────────────────────────────────────────────────────────────────────────
// 2026 refresh: moved off the red/burgundy palette to a clean nature-tech
// system. Existing semantic NAMES (burgundy / charcoal / mcgill_red / sand)
// are preserved so the dozen-or-so components that reference them keep
// working without touching every file. The values they map to are new.
// ─────────────────────────────────────────────────────────────────────────

const Text = defineStyleConfig({
    baseStyle: {
        fontFamily: "'Montserrat Variable', system-ui, sans-serif",
        color: 'text_black',
        letterSpacing: '-0.005em',
    }
})

const Heading = defineStyleConfig({
    baseStyle: {
        fontFamily: "'Montserrat Variable', system-ui, sans-serif",
        color: 'text_black',
        letterSpacing: '-0.02em',
        fontWeight: 700,
    }
})

const Divider = defineStyleConfig({
    defaultProps: { color: 'gray.200' }
})

const Button = defineStyleConfig({
    baseStyle: {
        fontFamily: "'Montserrat Variable', system-ui, sans-serif",
        fontWeight: 600,
        letterSpacing: '-0.005em',
        borderRadius: 'lg',
        transition: 'all .15s ease',
    },
    variants: {
        brandSolid: {
            bg: 'burgundy.500',
            color: 'white',
            _hover: { bg: 'burgundy.600', transform: 'translateY(-1px)', boxShadow: 'md' },
            _active: { bg: 'burgundy.700' },
            borderRadius: 'lg',
            fontSize: 18,
            px: 6,
            py: 6,
        },
        brandOutline: {
            bg: 'white',
            border: '1px solid',
            borderColor: 'gray.200',
            color: 'charcoal.500',
            _hover: { borderColor: 'burgundy.300', color: 'burgundy.600', bg: 'burgundy.50' },
            borderRadius: 'lg',
            fontSize: 16,
            fontWeight: 500,
        },
    }
})

export const theme = extendTheme({
    components: { Text, Heading, Button, Divider },

    fonts: {
        heading: "'Montserrat Variable', system-ui, sans-serif",
        body: "'Montserrat Variable', system-ui, sans-serif",
    },

    colors: {
        // Accent (was mcgill_red). Modern amber — used sparingly for highlights.
        mcgill_red: {
            _defaul: '#F59E0B',
            50:  '#FFFBEB',
            100: '#FEF3C7',
            200: '#FDE68A',
            300: '#FCD34D',
            400: '#FBBF24',
            500: '#F59E0B',
            600: '#D97706',
            700: '#B45309',
            800: '#92400E',
            900: '#78350F',
        },

        // Primary brand (was burgundy). Emerald — nature, life, conservation.
        burgundy: {
            _default: '#059669',
            50:  '#ECFDF5',
            100: '#D1FAE5',
            200: '#A7F3D0',
            300: '#6EE7B7',
            400: '#34D399',
            500: '#10B981',
            600: '#059669',
            700: '#047857',
            800: '#065F46',
            900: '#064E3B',
        },

        // Text & dark surfaces (was charcoal). Cooler slate scale.
        charcoal: {
            _default: '#0F172A',
            50:  '#F8FAFC',
            100: '#F1F5F9',
            200: '#E2E8F0',
            300: '#CBD5E1',
            400: '#94A3B8',
            500: '#0F172A',
            600: '#0B1220',
            700: '#080E1A',
        },

        // Backgrounds (was sand). Warmer stone-tinted neutrals.
        sand: {
            _default: '#FAFAF9',
            50:  '#FAFAF9',
            100: '#F5F5F4',
            200: '#E7E5E4',
        },

        soft_blue: '#E0F2FE', // sky-100
        blue:      '#BAE6FD', // sky-200
        dusk:      '#F0F9FF', // sky-50, used for sidebar tints

        text_black: '#0F172A', // slate-900 instead of pure black
    }
})
